import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import RemoveIcon from "@mui/icons-material/Remove";
import SaveIcon from "@mui/icons-material/Save";
import { Button, IconButton, TextField } from "@mui/material";
import Box from "@mui/material/Box";
import React from "react";
import { BingoResolution, getTitleToDisplay } from "./BingoResolution";
import { ColorPicker, isLightColor } from "./ColorPicker";
import { LastUpdatedAt } from "./utils";

interface ZoomedBingoSquareProps {
  bingoResolution: BingoResolution;
  onIncrement: () => void;
  onDecrement: () => void;
  onEdit: (
    newTitle: string,
    newGoalTally: number,
    newCurrentTally: number,
    newColor: string,
    newNote: string,
  ) => void;
  onClose: () => void;
}

export default function ZoomedBingoSquare({
  bingoResolution,
  onIncrement,
  onDecrement,
  onEdit,
  onClose,
}: ZoomedBingoSquareProps) {
  const [isEditModeOn, setIsEditModeOn] = React.useState(false);
  const [currentBingoResolution, setCurrentBingoResolution] = React.useState({
    title: bingoResolution.title,
    currentTally: bingoResolution.currentTally,
    goalTally: bingoResolution.goalTally,
    color: bingoResolution.color,
    note: bingoResolution.note,
    updatedAt: bingoResolution.updatedAt,
  });

  function toggleEditMode() {
    if (isEditModeOn) {
      // Only save changes if there are changes.
      if (
        currentBingoResolution.title.trim() !== bingoResolution.title ||
        currentBingoResolution.goalTally !== bingoResolution.goalTally ||
        currentBingoResolution.currentTally !== bingoResolution.currentTally ||
        currentBingoResolution.color !== bingoResolution.color ||
        currentBingoResolution.note.trim() !== bingoResolution.note
      ) {
        // Save changes
        onEdit(
          currentBingoResolution.title.trim(),
          currentBingoResolution.goalTally,
          currentBingoResolution.currentTally,
          currentBingoResolution.color,
          currentBingoResolution.note.trim(),
        );
      }
    }
    setIsEditModeOn(!isEditModeOn);
  }

  function updateCurrentBingoResolutionLocally(
    newTitle: string,
    newGoalTally: number,
    newCurrentTally: number,
    newColor: string,
    newNote: string,
  ) {
    setCurrentBingoResolution({
      title: newTitle,
      goalTally: newGoalTally,
      currentTally: newCurrentTally,
      color: newColor,
      note: newNote,
      updatedAt: new Date(),
    });
  }

  const hasReachedGoal =
    bingoResolution.currentTally >= bingoResolution.goalTally &&
    bingoResolution.goalTally > 0;
  const isLightColorValue = hasReachedGoal
    ? true
    : isLightColor(bingoResolution.color);

  return (
    <Box>
      <Box
        className={`border-2 text-center text-2xl ${
          hasReachedGoal ? "bg-green-200 font-bold" : ""
        } flex flex-col gap-4`}
        padding={2}
        borderRadius={2}
        style={
          hasReachedGoal
            ? undefined
            : {
                backgroundColor: bingoResolution.color,
                color: isLightColorValue ? "black" : "white",
              }
        }
      >
        <Box className="absolute right-6">
          <IconButton
            onClick={onClose}
            size="small"
            className="absolute"
            aria-label="close"
          >
            <CloseIcon
              className={`${isLightColorValue ? "text-black" : "text-white"}`}
            />
          </IconButton>
        </Box>
        <Box className="absolute left-6">
          <IconButton
            onClick={toggleEditMode}
            size="small"
            className="absolute"
            aria-label="edit"
          >
            {isEditModeOn ? (
              <SaveIcon
                className={`${isLightColorValue ? "text-black" : "text-white"}`}
              />
            ) : (
              <EditIcon
                className={`${isLightColorValue ? "text-black" : "text-white"}`}
              />
            )}
          </IconButton>
        </Box>
        <Box>
          <BingoSquareTitle
            isEditModeOn={isEditModeOn}
            title={currentBingoResolution.title}
            onTitleChange={(newTitle) =>
              updateCurrentBingoResolutionLocally(
                newTitle,
                currentBingoResolution.goalTally,
                currentBingoResolution.currentTally,
                currentBingoResolution.color,
                currentBingoResolution.note,
              )
            }
            isLightBackground={isLightColorValue}
          />
          {!isEditModeOn && (
            <LastUpdatedAt updatedAt={currentBingoResolution.updatedAt} />
          )}
        </Box>

        <BingoSquareNote
          isEditModeOn={isEditModeOn}
          note={currentBingoResolution.note}
          onNoteChange={(newNote) =>
            updateCurrentBingoResolutionLocally(
              currentBingoResolution.title,
              currentBingoResolution.goalTally,
              currentBingoResolution.currentTally,
              currentBingoResolution.color,
              newNote,
            )
          }
          isLightBackground={isLightColorValue}
        />

        <Box
          className={`flex flex-row ${
            isEditModeOn ? "justify-between" : "justify-center"
          } items-center w-full`}
        >
          {isEditModeOn && (
            <ColorPicker
              color={currentBingoResolution.color}
              onChange={(newColor) =>
                updateCurrentBingoResolutionLocally(
                  currentBingoResolution.title,
                  currentBingoResolution.goalTally,
                  currentBingoResolution.currentTally,
                  newColor,
                  currentBingoResolution.note,
                )
              }
            />
          )}

          <BingoSquareCounter
            isEditModeOn={isEditModeOn}
            currentTally={currentBingoResolution.currentTally}
            goalTally={currentBingoResolution.goalTally}
            color={bingoResolution.color}
            onIncrement={() => {
              onIncrement();
              updateCurrentBingoResolutionLocally(
                currentBingoResolution.title,
                currentBingoResolution.goalTally,
                currentBingoResolution.currentTally + 1,
                currentBingoResolution.color,
                currentBingoResolution.note,
              );
            }}
            onDecrement={() => {
              onDecrement();
              updateCurrentBingoResolutionLocally(
                currentBingoResolution.title,
                currentBingoResolution.goalTally,
                Math.max(0, currentBingoResolution.currentTally - 1),
                currentBingoResolution.color,
                currentBingoResolution.note,
              );
            }}
            onCurrentTallyChange={(newCurrentTally) =>
              updateCurrentBingoResolutionLocally(
                currentBingoResolution.title,
                currentBingoResolution.goalTally,
                newCurrentTally,
                currentBingoResolution.color,
                currentBingoResolution.note,
              )
            }
            onGoalTallyChange={(newGoalTally) =>
              updateCurrentBingoResolutionLocally(
                currentBingoResolution.title,
                newGoalTally,
                currentBingoResolution.currentTally,
                currentBingoResolution.color,
                currentBingoResolution.note,
              )
            }
          />
          {/* Placeholder div to get alignment right */}
          {/* {isEditModeOn && <Box width={40} height={40}></Box>} */}
          {isEditModeOn && (
            <Button onClick={toggleEditMode} variant="contained">
              Save
            </Button>
          )}
        </Box>
      </Box>
      {/* hidden box that also closes the zoomed bingo squae */}
      <Box onClick={onClose} className="w-full h-screen"></Box>
    </Box>
  );
}

interface BingoSquareTitleProps {
  isEditModeOn: boolean;
  title: string;
  onTitleChange: (newTitle: string) => void;
  // whether the background is light (true => use dark text)
  isLightBackground: boolean;
}

export function BingoSquareTitle({
  isEditModeOn,
  title,
  onTitleChange,
  isLightBackground,
}: BingoSquareTitleProps) {
  return (
    <Box sx={{ height: "100%" }}>
      {isEditModeOn ? (
        <TextField
          label="Title"
          value={title}
          onChange={(e) => onTitleChange(e.target.value)}
          variant="filled"
          size="medium"
          type="search"
          sx={{
            "& .MuiInputBase-input": {
              color: isLightBackground ? "black" : "white",
            },
          }}
        />
      ) : (
        <Box>{getTitleToDisplay(title)}</Box>
      )}
    </Box>
  );
}

interface BingoSquareNoteProps {
  isEditModeOn: boolean;
  note: string;
  onNoteChange: (newNote: string) => void;
  // whether the background is light (true => use dark text)
  isLightBackground: boolean;
}

export function BingoSquareNote({
  isEditModeOn,
  note,
  onNoteChange,
  isLightBackground,
}: BingoSquareNoteProps) {
  return (
    <Box sx={{ height: "100%" }}>
      {isEditModeOn ? (
        <TextField
          label="Note"
          multiline={true}
          value={note}
          onChange={(e) => onNoteChange(e.target.value)}
          variant="filled"
          size="medium"
          type="search"
          sx={{
            "& .MuiInputBase-input": {
              color: isLightBackground ? "black" : "white",
            },
          }}
        />
      ) : (
        <Box className="whitespace-pre-wrap italic">{note}</Box>
      )}
    </Box>
  );
}

interface BingoSquareCounterProps {
  isEditModeOn: boolean;
  currentTally: number;
  goalTally: number;
  color: string;
  onIncrement: () => void;
  onDecrement: () => void;
  onCurrentTallyChange: (newCurrentTally: number) => void;
  onGoalTallyChange: (newGoalTally: number) => void;
}

export function BingoSquareCounter({
  isEditModeOn,
  currentTally,
  goalTally,
  color,
  onIncrement,
  onDecrement,
  onCurrentTallyChange,
  onGoalTallyChange,
}: BingoSquareCounterProps) {
  const [currentTallyText, setCurrentTallyText] = React.useState(
    currentTally.toString(),
  );
  const [goalTallyText, setGoalTallyText] = React.useState(
    goalTally.toString(),
  );

  const isGoalReached = currentTally >= goalTally && goalTally > 0;
  const isLightColorValue = isGoalReached ? true : isLightColor(color);

  return (
    <Box display="flex" alignItems="center" justifyContent="center">
      {!isEditModeOn && (
        <IconButton
          aria-label="decrement"
          onClick={onDecrement}
          disabled={goalTally <= 0}
        >
          <RemoveIcon
            className={`${isLightColorValue ? "text-black" : "text-white"}`}
          />
        </IconButton>
      )}

      <Box className="text-4xl flex flex-row">
        {isEditModeOn ? (
          <TextField
            value={currentTallyText}
            label="Count"
            type="number"
            onChange={(e) => {
              const newCurrentTallyText = e.target.value;
              setCurrentTallyText(newCurrentTallyText);
              const newCurrentTallyNumber = parseInt(newCurrentTallyText, 10);
              if (!isNaN(newCurrentTallyNumber)) {
                onCurrentTallyChange(newCurrentTallyNumber);
              }
            }}
            variant="filled"
            size="small"
            sx={{
              width: 80,
              "& .MuiInputBase-input": {
                color: isLightColorValue ? "black" : "white",
              },
            }}
          />
        ) : (
          <Box>{currentTally}</Box>
        )}
        <Box>/</Box>
        {isEditModeOn ? (
          <TextField
            value={goalTallyText}
            type="number"
            label="Goal"
            onChange={(e) => {
              const newGoalTallyText = e.target.value;
              setGoalTallyText(newGoalTallyText);
              const newGoalTally = parseInt(newGoalTallyText, 10);
              if (!isNaN(newGoalTally)) {
                onGoalTallyChange(newGoalTally);
              }
            }}
            variant="filled"
            size="small"
            sx={{
              width: 80,
              "& .MuiInputBase-input": {
                color: isLightColorValue ? "black" : "white",
              },
            }}
          />
        ) : (
          <Box>{goalTally}</Box>
        )}
      </Box>

      {!isEditModeOn && (
        <IconButton aria-label="increment" onClick={onIncrement}>
          <AddIcon
            className={`${isLightColorValue ? "text-black" : "text-white"}`}
          />
        </IconButton>
      )}
    </Box>
  );
}
